import { T_IBalanceUpdater } from '@cgs/common';
import {
  BetCalculationType,
  FreeSpinsInfoEvent,
  InternalBet,
  SpinResponse,
} from '@cgs/data-access';
import { Container, EventDispatcher, EventStream, IDisposable } from '@cgs/wge';
import {
  GAME_STATE_MACHINE_TOKEN,
  GameStateMachine,
} from '../../reels_engine/state_machine/game-state-machine';
import {
  T_GameStateMachineNotifierComponent,
  T_ISlotPopupCoordinator,
  T_ISlotPrimaryAnimationProvider,
} from '../../type_definitions';
import { BET_STORAGE_STRATEGY, IBetStorageStrategy } from '../bet/bet-storage/bet-storage.strategy';
import { IBalanceUpdater } from '../components/balance_listener_provider';
import {
  AbstractListener,
  GameStateMachineNotifierComponent,
} from '../components/game_state_machine_notifier_component';
import { ISlotPrimaryAnimationProvider } from '../components/interfaces/i_slot_primary_animation_provider';
import {
  INotEnoughBalancePopupComponent,
  NOT_ENOUGH_BALANCE_POPUP_COMPONENT_TOKEN,
} from '../components/not-enough-balance-popup.component';
import { SlotPopups } from './slot/views/base_popup_view';
import { ISlotPopupCoordinator } from './slot_popup_coordinator';
import {
  BET_STORE_TOKEN,
  BetStore,
  LAUNCH_PARAMS_STORE_TOKEN,
  LaunchParamsStore,
  SLOT_MACHINE_STORE_TOKEN,
  SlotMachineStore,
} from '../../store';
import { GameComponentProvider } from '../components/game_component_provider';

export enum SlotSessionProperties {
  TotalWin = 'TotalWin',
  AddTotalWin = 'addTotalWin',
  AddCurrentWin = 'AddCurrentWin',
  ResetWinState = 'ResetWinState',
  Lines = 'Lines',
  Bet = 'Bet',
  BetMultiplier = 'BetMultiplier',
  TotalBet = 'TotalBet',
  Bets = 'Bets',
  BetIndex = 'BetIndex',
  NewBetAvailable = 'NewBetAvailable',
  UpdateMaxBet = 'UpdateMaxBet',
  UserMaxBet = 'UserMaxBet',
  NewConfiguredBetsAvailable = 'NewConfiguredBetsAvailable',
}

export const SLOT_SESSION_TOKEN = Symbol('SLOT_SESSION_TOKEN');

export class SlotSession extends GameComponentProvider implements AbstractListener, IDisposable {
  public betType: string = 'custom';

  protected readonly _container: Container;
  private _gameStateMachine: GameStateMachine<any>;
  protected _balanceUpdater: IBalanceUpdater;
  private _slotPopupCoordinator: ISlotPopupCoordinator;
  private betStorage: IBetStorageStrategy | null;
  protected _notEnoughBalancePopup: INotEnoughBalancePopupComponent;

  private _totalWin: number;
  private _currentWin: number;
  private _lines: number;
  private _bet: InternalBet | null = null;
  private _totalBet: number = 0.0;
  private _currentBetMultiplier: number = 1;
  private _currentBetIndex: number;
  private _propertyChangedController: EventDispatcher<SlotSessionProperties> =
    new EventDispatcher();
  private readonly launchParamsStore: LaunchParamsStore;
  private readonly slotMachineStore: SlotMachineStore;
  private readonly betStore: BetStore;

  constructor(container: Container) {
    super();

    this._container = container;
    this.launchParamsStore = container.resolve<LaunchParamsStore>(LAUNCH_PARAMS_STORE_TOKEN);
    this.slotMachineStore = container.resolve<SlotMachineStore>(SLOT_MACHINE_STORE_TOKEN);
    this.betStore = container.resolve<BetStore>(BET_STORE_TOKEN);
  }

  public initialize(): void {
    this._gameStateMachine =
      this._container.resolve<GameStateMachine<SpinResponse>>(GAME_STATE_MACHINE_TOKEN);
    this._slotPopupCoordinator =
      this._container.resolve<ISlotPopupCoordinator>(T_ISlotPopupCoordinator);
    this.betStorage = this._container.resolve<IBetStorageStrategy>(BET_STORAGE_STRATEGY, {
      optional: true,
    });
    this._balanceUpdater = this._container.resolve<IBalanceUpdater>(T_IBalanceUpdater);
    const notifierComponent = this._container.resolve<GameStateMachineNotifierComponent>(
      T_GameStateMachineNotifierComponent
    );
    notifierComponent.notifier.addListener(this);

    this.currentBet = this.getInitialBet();

    const machineState = this.slotMachineStore.state;
    if (machineState.freeSpinsInfo?.currentFreeSpinsGroup) {
      const fsg = machineState.freeSpinsInfo.currentFreeSpinsGroup;
      this.currentBet = this.createCurrentBetBasedOnResponse(fsg.bet, -1.0, fsg.betCalculation);
    } else if (typeof machineState.bonusInfo?.result?.bet === 'number') {
      this.currentBet = this.createCurrentBetBasedOnResponse(
        machineState.bonusInfo.result.bet,
        -1.0,
        machineState.freeSpinsInfo?.currentFreeSpinsGroup?.betCalculation || ''
      );
    }

    this.updateTotalBet();
    this.setTotalWin(0);
    this._currentBetIndex = this.Bets.indexOf(this.currentBet);
    this.setLines(this.launchParamsStore.state.maxLines);
    this.setBetMultiplier(this.launchParamsStore.state.betMultiplier);
  }

  public onStateEntered(_slotState: string): void {}
  public onStateExited(_slotState: string): void {}

  public get gameStateMachine(): GameStateMachine<SpinResponse> {
    return this._gameStateMachine;
  }

  public get notEnoughBalancePopup(): INotEnoughBalancePopupComponent {
    if (!this._notEnoughBalancePopup) {
      this._notEnoughBalancePopup = this._container.resolve<INotEnoughBalancePopupComponent>(
        NOT_ENOUGH_BALANCE_POPUP_COMPONENT_TOKEN
      );
    }
    return this._notEnoughBalancePopup;
  }

  public get totalWin(): number {
    return this._totalWin;
  }

  public get currentWin(): number {
    return this._currentWin;
  }

  public get lines(): number {
    return this._lines;
  }

  public get bet(): InternalBet {
    return this._bet!;
  }

  public get totalBet(): number {
    return this._totalBet;
  }

  public get currentBetMultiplier(): number {
    return this._currentBetMultiplier;
  }

  public get propertyChanged(): EventStream<SlotSessionProperties> {
    return this._propertyChangedController.eventStream;
  }

  public get currentBet(): InternalBet {
    return this._bet!;
  }

  public set currentBet(bet: InternalBet) {
    this._bet = bet;
    this.betStore.update({ bet: bet.bet });
  }

  public get isMaxBet(): boolean {
    return this.getMaxBet() === this._bet;
  }

  public get isXtremeBetNow(): boolean {
    return !!this._bet?.isXtremeBet;
  }

  public get isMinBet(): boolean {
    const localBets = this.Bets.filter((b) => !b.isExtraBet);
    localBets.sort();
    return localBets[0] == this._bet;
  }

  public SetBet(bet: InternalBet): void {
    if (this._bet?.bet === bet.bet) {
      return;
    }

    this.tryShowMaxBetExceeded(bet);

    this._currentBetIndex = this.Bets.indexOf(this._bet!);
    this.updateTotalBet();
    this.updateBetType();

    this.betStorage?.save(this._bet!.bet);

    this._propertyChangedController.dispatchEvent(SlotSessionProperties.Bet);
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.Bets);
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.BetIndex);

    const slotActionProvider = this._container.resolve<ISlotPrimaryAnimationProvider>(
      T_ISlotPrimaryAnimationProvider,
      { optional: true }
    );
    if (slotActionProvider) {
      slotActionProvider.clearWinLines();
    }
  }

  private tryShowMaxBetExceeded(bet: InternalBet): void {
    const maxBet = this.getMaxBet();

    if (this.currentBet === maxBet && bet.isExtraBet) {
      this._slotPopupCoordinator.onPopupShown(SlotPopups.MaxBetExceeded);
      this.currentBet = maxBet;
      this._slotPopupCoordinator.onPopupHidden(SlotPopups.MaxBetExceeded);
    } else {
      this.currentBet = bet;
    }
  }

  public setBetMultiplier(betMultiplier: number): void {
    this._currentBetMultiplier = betMultiplier;
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.BetMultiplier);
  }

  private updateBetType(): void {
    if (this._bet?.bet === this.launchParamsStore.state.defaultBet) {
      this.betType = 'default';
      return;
    }

    if (this.Bets.length > 0) {
      if (this.Bets[this.Bets.length - 1] === this._bet) {
        this.betType = 'max';
        return;
      }
      if (this.Bets[0] == this._bet) {
        this.betType = 'min';
        return;
      }
    }

    this.betType = 'custom';
  }

  public get BetIndex(): number {
    return this._currentBetIndex;
  }

  public get Bets(): InternalBet[] {
    return this.launchParamsStore.state.bets;
  }

  public setLines(line: number): void {
    if (this._lines != line) {
      this._lines = line;
      this._propertyChangedController.dispatchEvent(SlotSessionProperties.Lines);
      this.updateTotalBet();
    }
  }

  public setTotalWin(totalWin: number): void {
    if (typeof totalWin === 'number') {
      this._totalWin = totalWin;
      this._propertyChangedController.dispatchEvent(SlotSessionProperties.TotalWin);
    }
  }

  public addCurrentWin(totalWin: number, currentWin: number): void {
    if (typeof totalWin === 'number' && typeof currentWin === 'number') {
      this._totalWin = totalWin;
      this._currentWin = currentWin;
      this._propertyChangedController.dispatchEvent(SlotSessionProperties.AddCurrentWin);
    }
  }

  public addTotalWin(totalWin: number, currentWin: number): void {
    if (typeof totalWin === 'number' && typeof currentWin === 'number') {
      this._totalWin = totalWin;
      this._currentWin = currentWin;
      this._propertyChangedController.dispatchEvent(SlotSessionProperties.AddTotalWin);
    }
  }

  public get IsBalanceEnough(): boolean {
    const freeSpinsInfo = this._gameStateMachine.curResponse.freeSpinsInfo;

    // Check if we should validate balance:
    // 1. No free spins info exists, OR
    // 2. Current free spins count is 0, OR
    // 3. Free spins have finished (event is 'finished')
    if (
      !freeSpinsInfo ||
      freeSpinsInfo.currentFreeSpinsGroup.count == 0 ||
      freeSpinsInfo.event === FreeSpinsInfoEvent.FreeSpinsFinished
    ) {
      try {
        return this._balanceUpdater.isBalanceEnough(this.totalBet);
      } catch (e) {
        this.notEnoughBalancePopup.show();
        return false;
      }
    }
    return true;
  }

  public decreaseBalanceAndStopUpdate(): void {
    const freeSpinsInfo = this._gameStateMachine.curResponse.freeSpinsInfo;

    // Decrease balance normally if:
    // 1. No free spins info exists, OR
    // 2. Free spins have finished (event is 'finished')
    if (!freeSpinsInfo || freeSpinsInfo.event === FreeSpinsInfoEvent.FreeSpinsFinished) {
      try {
        this._balanceUpdater.decreaseBalanceAndStopUpdate(this.totalBet);
      } catch (e) {
        this.notEnoughBalancePopup.show();
      }
    } else {
      // During active free spins, don't decrease balance
      this._balanceUpdater.decreaseBalanceAndStopUpdate(0);
    }
  }

  public updateBalance(): void {
    this._balanceUpdater.updateBalance();
  }

  public updateTotalBet(): void {
    if (!this._bet || !this._lines) {
      return;
    }
    this._totalBet = this._bet.bet * this._lines;
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.TotalBet);
  }

  public updateTotalBetEvent(): void {
    if (!this._bet || !this._lines) {
      return;
    }
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.TotalBet);
  }

  public async changeBetIndex(indexDelta: number): Promise<void> {
    const bets = this.Bets;
    const maxIndex = bets.length - 1;

    const currentIndex = bets.findIndex(
      (bet: InternalBet) => this._bet && bet.bet === this._bet.bet
    );

    const newIndex = currentIndex + indexDelta;

    if (newIndex >= 0 && newIndex <= maxIndex) {
      this.SetBet(bets[newIndex]);
      this._propertyChangedController.dispatchEvent(SlotSessionProperties.BetIndex);
    }
  }

  public async setMaxBet(): Promise<void> {
    this.SetBet(this.getMaxBet());
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.BetIndex);
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.UserMaxBet);
  }

  public async setMaxFeatureBet(): Promise<void> {
    this.SetBet(this.getMaxFeatureBet());
    this._propertyChangedController.dispatchEvent(SlotSessionProperties.BetIndex);
  }

  private getMaxFeatureBet(): InternalBet {
    return this.getMaxBet();
  }

  public getInitialBet(): InternalBet {
    const defaultBet =
      this.Bets.find((arg) => arg.bet === this.launchParamsStore.state.defaultBet) || this.Bets[0];

    const storageBet = this.betStorage?.getSaved();

    if (!storageBet) {
      return defaultBet;
    }

    const savedBet = this.Bets.find((bet) => bet.bet === storageBet);
    return savedBet || defaultBet;
  }

  public getMaxBet(): InternalBet {
    const localBets = this.Bets.filter((b) => !b.isExtraBet && !b.isXtremeBet);
    localBets.sort();
    return localBets[localBets.length - 1];
  }

  public getMinBet(): InternalBet {
    const localBets = this.Bets.filter((b) => !b.isExtraBet);
    localBets.sort();
    return localBets[0];
  }

  public isBalanceEnough(sum: number): boolean {
    return this._balanceUpdater.isBalanceEnough(sum);
  }

  public dispose(): void {
    if (this._balanceUpdater) {
      this._balanceUpdater.unlockBalance();
    }
  }

  public deinitialize() {
    this.dispose();
  }

  private createCurrentBetBasedOnResponse(
    bet: number,
    effectiveBet: number,
    calculationType: string | null
  ): InternalBet {
    const restoredBet = new InternalBet();
    restoredBet.bet = bet;
    restoredBet.effectiveBet = effectiveBet;

    switch (calculationType) {
      case 'AVERAGE':
        restoredBet.calculationType = BetCalculationType.AVERAGE;
        break;
      case 'FREE_SPINS':
        restoredBet.calculationType = BetCalculationType.FREESPINS;
        break;
      case 'DEFAULT':
      default:
        restoredBet.calculationType = BetCalculationType.DEFAULT;
        break;
    }
    return restoredBet;
  }
}
